// src/pages/LoginFlow/AcceptInvitePage.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import {
  acceptOrganizationInvite,
  validateInviteToken,
} from "../../api/organizations/organizations";
import RandomQuote from "../../components/RandomQuote/RandomQuote";
import Spinner from "../../components/Spinner/Spinner";
import BrandTitle from "../../components/BrandTitle/BrandTitle";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validating, setValidating] = useState(true);

  // Validate invite and check user status
  useEffect(() => {
    const validateAndRedirect = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setValidating(false);
        return;
      }

      try {
        const validation = await validateInviteToken(token);

        // If user exists for this email
        if (validation.userExists) {
          // If already logged in
          if (isAuthenticated && user) {
            // Check if logged-in user's email matches the invited email
            if (
              user.email.toLowerCase().trim() !==
              validation.email.toLowerCase().trim()
            ) {
              setError(
                `This invitation was sent to ${validation.email}. Please log out and log in with that email address.`,
              );
              setValidating(false);
              return;
            }

            try {
              console.log(
                "AcceptInvitePage - Auto-accepting invite for logged-in user:",
                user.email,
              );

              // Auto-accept the invite
              await acceptOrganizationInvite(token, {
                existingUserId: user.id,
              });

              console.log(
                "AcceptInvitePage - Invite accepted, refreshing user data",
              );

              // Refresh user data using the new method
              const authStore = useAuthStore.getState();
              await authStore.refreshUserData();

              console.log(
                "AcceptInvitePage - Data refreshed, navigating to tasks",
              );

              // Navigate to tasks
              navigate("/boards");
              return;
            } catch (err) {
              console.error("AcceptInvitePage - Auto-accept error:", err);
              setError(err.message || "Failed to accept invitation");
              setValidating(false);
              return;
            }
          } else {
            // User exists but not logged in - redirect to login
            navigate(`/login?redirect=accept-invite&token=${token}`);
            return;
          }
        }

        // User doesn't exist - show registration form
        setInviteInfo({
          isLoggedIn: false,
          email: validation.email,
          organizationName: validation.organizationName,
          projectName: validation.projectName,
          inviteType: validation.inviteType,
        });
        setValidating(false);
      } catch (err) {
        setError(err.message || "Failed to validate invitation");
        setValidating(false);
      }
    };

    validateAndRedirect();
  }, [token, isAuthenticated, user, navigate]);

  // Show loading spinner while validating
  if (validating) {
    return <Spinner text="Validating invitation..." />;
  }

  const handleAcceptForNewUser = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !password) {
      setError("All fields are required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await acceptOrganizationInvite(token, {
        firstName,
        lastName,
        password,
      });

      // Log the new user in using the email from the response
      const authStore = useAuthStore.getState();
      await authStore.login(result.userEmail, password);

      // Navigate to tasks
      navigate("/tasks");
    } catch (err) {
      setError(err.message || "Failed to accept invitation");
    } finally {
      setLoading(false);
    }
  };

  // Show error state
  if (error && !inviteInfo) {
    return (
      <div className="authentication-view">
        <div className="left-wrapper">
          <div className="title">
            <BrandTitle fontSize={40} className="brand-title-login" />
          </div>
          <div className="quote">
            <RandomQuote />
          </div>
        </div>
        <div className="right-wrapper">
          <div className="title">Invalid Invitation</div>
          <p style={{ color: "var(--error-color)", marginTop: "20px" }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  // Only show registration form if user doesn't exist
  if (!inviteInfo) {
    return null;
  }

  // User is not logged in and doesn't exist - show registration form
  return (
    <div className="authentication-view">
      <div className="left-wrapper">
        <div className="title">
          <BrandTitle fontSize={40} className="brand-title-login" />
        </div>
        <div className="quote">
          <RandomQuote />
        </div>
      </div>
      <div className="right-wrapper">
        <div className="title">Accept Invitation</div>
        <p style={{ marginBottom: "20px", fontSize: "14px" }}>
          Create your account to accept the invitation.
        </p>
        <form onSubmit={handleAcceptForNewUser}>
          <input
            className="flow-form"
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="off"
            required
          />
          <br />
          <input
            className="flow-form"
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="off"
            required
          />
          <br />
          <input
            className="flow-form"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <br />
          <button type="submit" className="flow-button" disabled={loading}>
            Accept & Create Account
          </button>
        </form>
        {loading && <Spinner text="Creating your account..." />}
        {error && (
          <span
            style={{
              color: "var(--error-color)",
              marginTop: "10px",
              display: "block",
            }}
          >
            {error}
          </span>
        )}
        <p style={{ marginTop: "20px", fontSize: "14px" }}>
          Already have an account?{" "}
          <a className="link" href="/login">
            Login
          </a>
        </p>
        <div className="logo">
          <img
            src="/somigo_logo.svg"
            alt="SOMIGO Icon"
            width="100"
            height="100"
          />
        </div>
      </div>
    </div>
  );
}
